/* DietXP Metabolic Energy Profiler — pure clinical calculation rules.
   Keep this file DOM-free so the same functions can be regression-tested in Node.
   Guideline review: 2026-06-21. This module does not replace clinician review. */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.DietXPClinicalRules = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var VERSION = '3.2.0-preclinical';
  var REVIEW_DATE = '2026-06-21';
  var SOURCES = {
    weir: 'Weir JB. J Physiol. 1949;109:1-9. doi:10.1113/jphysiol.1949.sp004363',
    mifflin: 'Mifflin MD et al. Am J Clin Nutr. 1990;51:241-247. PMID:2305711',
    icu: 'Singer P et al. ESPEN practical and partially revised guideline: Clinical nutrition in the intensive care unit. Clin Nutr. 2023;42:1671-1689. doi:10.1016/j.clnu.2023.07.011',
    steadyState: 'Academy of Nutrition and Dietetics Evidence Analysis Library: IC steady-state methods.',
    kidney: 'KDOQI Clinical Practice Guideline for Nutrition in CKD: 2020 Update.'
  };

  function weirAbbreviated(vo2, vco2) {
    return ((3.941 * vo2) + (1.106 * vco2)) * 1440;
  }

  function weirComplete(vo2, vco2, urinaryNitrogen) {
    return weirAbbreviated(vo2, vco2) - (2.17 * urinaryNitrogen);
  }

  function vcoMultiplier(rq) {
    var assumedRQ = rq || 0.86;
    return ((3.941 / assumedRQ) + 1.106) * 1.44;
  }

  function vco2Only(vco2mLMin, rq) {
    return vco2mLMin * vcoMultiplier(rq);
  }

  function mifflinStJeor(weight, heightCm, age, sex) {
    var base = (10 * weight) + (6.25 * heightCm) - (5 * age);
    return sex === 'M' ? base + 5 : base - 161;
  }

  function harrisBenedict(weight, heightCm, age, sex) {
    return sex === 'M'
      ? 88.362 + (13.397 * weight) + (4.799 * heightCm) - (5.677 * age)
      : 447.593 + (9.247 * weight) + (3.098 * heightCm) - (4.330 * age);
  }

  function henryOxford(weight, heightCm, age, sex) {
    if (sex === 'M') {
      if (age < 30) return (14.4 * weight) + (3.13 * heightCm) + 113;
      if (age < 60) return (11.4 * weight) + (5.41 * heightCm) - 137;
      return (11.4 * weight) + (5.41 * heightCm) - 256;
    }
    if (age < 30) return (10.4 * weight) + (6.15 * heightCm) - 282;
    if (age < 60) return (8.18 * weight) + (5.02 * heightCm) - 11.6;
    return (8.52 * weight) + (4.21 * heightCm) + 10.7;
  }

  function schofieldWeightOnly(weight, age, sex) {
    if (sex === 'M') {
      if (age < 30) return (15.057 * weight) + 692.2;
      if (age < 60) return (11.472 * weight) + 873.1;
      return (11.711 * weight) + 587.7;
    }
    if (age < 30) return (14.818 * weight) + 486.6;
    if (age < 60) return (8.126 * weight) + 845.6;
    return (9.082 * weight) + 658.5;
  }

  function icmrNIN2020(weight, age, sex) {
    return schofieldWeightOnly(weight, age, sex) * (sex === 'M' ? 0.90 : 0.91);
  }

  function schofieldPediatric(weight, heightM, age, sex) {
    if (age >= 18) return null;
    if (sex === 'M') {
      if (age < 3) return (0.167 * weight) + (1517.4 * heightM) - 617.6;
      if (age < 10) return (19.6 * weight) + (130.3 * heightM) + 414.9;
      return (16.25 * weight) + (137.2 * heightM) + 515.5;
    }
    if (age < 3) return (16.25 * weight) + (1023.2 * heightM) - 413.5;
    if (age < 10) return (16.97 * weight) + (161.8 * heightM) + 371.2;
    return (8.365 * weight) + (465 * heightM) + 200;
  }

  function cunningham(lbm) { return 500 + (22 * lbm); }
  function katchMcArdle(lbm) { return 370 + (21.6 * lbm); }
  function bmi(weight, heightCm) { var h = heightCm / 100; return weight / (h * h); }
  function devineIBW(heightCm, sex) { var inches = heightCm / 2.54; return (sex === 'M' ? 50 : 45.5) + 2.3 * (inches - 60); }
  function adjustedWeight(actual, ibw, factor) { return ibw + (factor == null ? 0.25 : factor) * (actual - ibw); }
  function weightLossPercent(previous, current) { return previous > 0 ? ((previous - current) / previous) * 100 : null; }
  function leanMassFromBodyFat(actual, bodyFatPct) { return actual * (1 - (bodyFatPct / 100)); }
  function fatMass(actual, lbm) { return actual - lbm; }

  function steadyStateCriterion(durationMinutes) {
    if (!(durationMinutes >= 5)) return { maxCV: null, label: 'At least 5 minutes required' };
    if (durationMinutes >= 25) return { maxCV: 10, label: '25+ minutes: VO2 and VCO2 CV ≤10%' };
    return { maxCV: 5, label: '5–24 minutes: VO2 and VCO2 CV ≤5%' };
  }

  function evaluateICQuality(input) {
    var critical = [];
    var caution = [];
    var criterion = steadyStateCriterion(Number(input.durationMinutes));
    var vo2CV = Number(input.vo2CV);
    var vco2CV = Number(input.vco2CV);
    var fio2 = Number(input.fio2);
    var deviceMax = Number(input.deviceFiO2Max);

    if (!Number(input.durationMinutes)) caution.push('measurement duration not documented');
    else if (Number(input.durationMinutes) < 5) critical.push('measurement duration below 5 minutes');

    if (!Number.isFinite(vo2CV) || vo2CV <= 0) caution.push('VO2 CV not documented');
    if (!Number.isFinite(vco2CV) || vco2CV <= 0) caution.push('VCO2 CV not documented');
    if (criterion.maxCV != null) {
      if (vo2CV > criterion.maxCV) critical.push('VO2 CV exceeds ' + criterion.maxCV + '% criterion');
      if (vco2CV > criterion.maxCV) critical.push('VCO2 CV exceeds ' + criterion.maxCV + '% criterion');
    }

    if (input.steadyState === 'no') critical.push('steady state not achieved');
    else if (!input.steadyState || input.steadyState === 'unknown') caution.push('steady state not assessed');
    if (input.calibration === 'failed') critical.push('device calibration failed');
    else if (!input.calibration || input.calibration === 'not_performed') caution.push('device calibration not documented');
    if (input.airLeak === 'confirmed') critical.push('confirmed circuit or interface leak');
    else if (input.airLeak === 'suspected') caution.push('suspected circuit or interface leak');

    if (fio2 > 0) {
      if (deviceMax > 0 && fio2 > deviceMax) critical.push('FiO2 exceeds the entered device-validated limit');
      else if (deviceMax > 0 && fio2 >= deviceMax - 0.05) caution.push('FiO2 is close to the entered device-validated limit');
      else if (!(deviceMax > 0)) caution.push('device-validated FiO2 limit not documented');
    }
    return { critical: critical, caution: caution, criterion: criterion };
  }

  return {
    version: VERSION,
    reviewDate: REVIEW_DATE,
    sources: SOURCES,
    equations: {
      weirAbbreviated: weirAbbreviated,
      weirComplete: weirComplete,
      vcoMultiplier: vcoMultiplier,
      vco2Only: vco2Only,
      mifflinStJeor: mifflinStJeor,
      harrisBenedict: harrisBenedict,
      henryOxford: henryOxford,
      icmrNIN2020: icmrNIN2020,
      schofieldPediatric: schofieldPediatric,
      cunningham: cunningham,
      katchMcArdle: katchMcArdle,
      schofieldWeightOnly: schofieldWeightOnly
    },
    anthropometry: {
      bmi: bmi,
      devineIBW: devineIBW,
      adjustedWeight: adjustedWeight,
      weightLossPercent: weightLossPercent,
      leanMassFromBodyFat: leanMassFromBodyFat,
      fatMass: fatMass
    },
    quality: { steadyStateCriterion: steadyStateCriterion, evaluateICQuality: evaluateICQuality }
  };
}));
